import { ContentStepBackground, FieldTypeLink } from '@allesauto/utils/consts';
import { getUrlFromStoryblokLink } from '@allesauto/utils/funcs';
import { sbEditable } from '@storyblok/storyblok-editable';
import clsx from 'clsx';

import ContentStep, { ContentStepProps } from './ContentStep';
import TextLink from '../textLink';

export interface ContentStepsProps {
  headline: string;
  background?: ContentStepBackground;
  buttonLabel?: string;
  buttonLink?: FieldTypeLink;
  steps: ContentStepProps[];
  iconDisabled?: boolean;
  hasPadding?: boolean;
  smallerTopSpace?: boolean;
  component: string;
  _uid: string;
  _editable?: string;
}

const ContentSteps = (props: ContentStepsProps) => {
  const {
    headline,
    background = ContentStepBackground.GREEN,
    buttonLabel,
    buttonLink,
    steps,
    iconDisabled = false,
    hasPadding = true,
    smallerTopSpace = false,
  } = props;

  const bg =
    background in ContentStepBackground
      ? background
      : ContentStepBackground.GREEN;

  const themes = {
    GREEN: {
      container: 'bg-green60 monochrome:bg-black',
    },
    GREY: {
      container: 'bg-grey10 monochrome:bg-black',
    },
    WHITE: {
      container: 'bg-white monochrome:bg-black',
    },
  };

  const classNames = {
    stepsWrapper: clsx(
      'grid',
      steps.length > 3
        ? 'sm:gap-x-0 lg:grid-cols-4 lg:gap-x-60px'
        : 'sm:gap-x-0 lg:grid-cols-3 lg:gap-x-[120px]'
    ),
    content: clsx(
      'w-full sm:px-15px sm:py-40px lg:px-60px',
      themes[bg].container,
      iconDisabled && hasPadding && 'lg:pt-[100px] lg:pb-80px',
      !iconDisabled && hasPadding && 'lg:py-80px',
      smallerTopSpace && 'lg:pt-[70px] lg:pb-[65px]'
    ),
    headerContent:
      'flex lg:items-center sm:flex-col lg:flex-row justify-between sm:mb-0',
    headline: 'text-purple80 monochrome:text-white text-headline4',
    textLinkContent: 'sm:mt-20px lg:mt-0',
  };

  return (
    <div {...sbEditable(props)} className={classNames.content}>
      <div className={classNames.headerContent}>
        <h3 className={classNames.headline}>{headline}</h3>
        {buttonLabel && (
          <div className={classNames.textLinkContent}>
            <TextLink
              text={buttonLabel}
              href={buttonLink ? getUrlFromStoryblokLink(buttonLink) : '#'}
            />
          </div>
        )}
      </div>
      <div className={classNames.stepsWrapper}>
        {steps &&
          steps.length > 0 &&
          steps.map(({ title, description, icon }, index: number) => (
            <ContentStep
              key={index}
              number={!iconDisabled && !icon ? `0${index + 1}` : undefined}
              icon={!iconDisabled && icon ? icon : undefined}
              title={title}
              description={description}
              smallerTopSpace={smallerTopSpace}
            />
          ))}
      </div>
    </div>
  );
};

export default ContentSteps;
